'use client';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { useAccountUm } from '@/hooks/use-account-um';
import { useGlobalContext } from '@/store/contexts/global-context';
import { ddRumError } from '@/utils/datadog';
import { DD_USE_CASES } from '@/utils/datadog/types';
import { LIVESTREAM_PAGE_ENTRY_POINT_DEFAULTS } from './constants';
import './styles/@theme';

// streaming-um transitively accesses `window` through core-ioc, so this must remain client-only.
const LiveStreamEntryPoint = dynamic(
    () => import('@fanduel/casino-streaming-um-web').then((module) => module.LiveStreamEntryPoint),
    { ssr: false }
);

type LivestreamPageEntryPointZoneProps = {
    backgroundImageUrl?: string;
    promoImageUrl?: string;
    description?: string;
    categoryPath?: string;
    label?: string;
    previewImageUrl?: string;
    avatarUrl?: string;
    previewTitle?: string;
};

export const LivestreamPageEntryPointZone = ({
    backgroundImageUrl,
    promoImageUrl,
    description,
    categoryPath,
    label,
    previewImageUrl,
    avatarUrl,
    previewTitle = 'Casino Livestream'
}: LivestreamPageEntryPointZoneProps) => {
    const router = useRouter();
    const { isLoggedIn } = useGlobalContext();
    const { triggerLogin } = useAccountUm();

    if (!backgroundImageUrl || !promoImageUrl || !previewImageUrl || !categoryPath) {
        // Missing required data indicates a CMS configuration problem, so it's worth reporting to Datadog RUM
        // from the client to help diagnose broken livestream entry points reported from the field.
        ddRumError({
            name: 'LIVESTREAM_PAGE_ENTRY_POINT_MISSING_CMS_DATA',
            description: 'Livestream page entry point is missing required CMS data',
            use_case: DD_USE_CASES.STREAMING,
            context: {
                hasBackgroundImageUrl: Boolean(backgroundImageUrl),
                hasPromoImageUrl: Boolean(promoImageUrl),
                hasPreviewImageUrl: Boolean(previewImageUrl),
                hasCategoryPath: Boolean(categoryPath)
            }
        });
        return null;
    }

    const handleCtaClick = () => {
        if (!isLoggedIn) {
            triggerLogin({ externalReferrer: categoryPath.replace(/^\/+/, '') });
            return;
        }

        router.push(categoryPath);
    };

    return (
        <div className="livestream-page-entry-point" data-testid="livestream-page-entry-point-zone">
            <LiveStreamEntryPoint
                label={label ? { text: label, style: { color: 'var(--livestream-zone-title-color)' } } : undefined}
                backgroundImage={{ src: backgroundImageUrl }}
                preview={{
                    image: { src: previewImageUrl },
                    avatar: avatarUrl ? { src: avatarUrl } : undefined,
                    title: previewTitle
                }}
                details={{
                    promoImage: { src: promoImageUrl },
                    description
                }}
                cta={{
                    label: LIVESTREAM_PAGE_ENTRY_POINT_DEFAULTS.CTA_LABEL,
                    onClick: handleCtaClick
                }}
            />
        </div>
    );
};
